/**
 * Разводящая → страница продукта без перезагрузки (заказчик, 23.10.2026:
 * «при клике на один из продуктов должна бесшовно открываться соответствующая
 * страница … со сменой URL через JS»).
 *
 * Как устроено:
 *   1. после загрузки разводящей обе страницы продуктов скачиваются заранее
 *      (fetch → DOMParser), а картинки их первого экрана — в кэш браузера;
 *   2. по клику секция разводящей становится fixed-слоем, а под ней
 *      `.l-content` подменяется контентом страницы продукта; адрес, title,
 *      description и canonical — из скачанного документа (history.pushState);
 *   3. модули переинициализируются на новом DOM (lazyLoading.refresh и т. д.);
 *   4. анимация 0.7s, cubic-bezier(0.71, 0.48, 0.28, 0.88): выбранная половина
 *      раскрывается на весь экран, вторая уезжает, тексты гаснут, пачка и нога
 *      перелетают на свои места в hero продукта (FLIP по коробкам —
 *      data-choose-box / data-choose-target-box(es); нога T — на кадр фона
 *      hero, по варианту кадра), затем слой тает
 *      (CSS: blocks/choose.styl);
 *   5. «назад» в браузере — обычная перезагрузка разводящей.
 *
 * HTML продуктов качается сразу при инициализации (картинки их первого экрана —
 * после load разводящей); клик до ответа ждёт его до WAIT мс (is-pending — тексты
 * гаснут), не дождался или нет prefetch (сеть, старый браузер) — обычный переход.
 * На Bitrix load приходит поздно (счётчики, jQuery с CDN, ядро), а страница продукта
 * отдаётся ~1 с — со стартом по load переход почти всегда шёл без анимации (23.10.2026).
 * Клик ДО загрузки самой сборки (defer-скрипт на Bitrix приходит после картинок и ядра)
 * ловит инлайн-скрипт в index.php: гасит тексты (is-pending), запоминает href в
 * window.chooseEarlyHref — здесь на старте переход доигрывается тем же ожиданием.
 */
itsoftApp.createModule("chooseProduct", function () {
    const module = this;

    const section = document.querySelector(".js-choose");

    if (!section) {
        return false;
    }

    const LEAVING_CLASS = "is-leaving";
    const FADING_CLASS = "is-fading";
    const TIME = 700;
    const FADE_DELAY = 350;
    const PENDING_CLASS = "is-pending";
    const WAIT = 3000; // сколько ждём prefetch после клика
    const MOBILE_QUERY = "(max-width: 1023px) and (orientation: portrait)"; // карточки стопкой (choose-wide в blocks/choose.styl)

    const supported = "fetch" in window && "DOMParser" in window && "pushState" in window.history &&
        "Promise" in window;

    const pages = {};

    /* Картинки первого экрана продукта — в кэш, чтобы под слоем перехода был
       не пустой hero. Берём источники, подходящие текущему окну и плотности */
    const warmImages = function (doc) {
        const pictures = doc.querySelectorAll(".intro picture, .t-intro picture");
        const dpr = window.devicePixelRatio > 1.5 ? "2x" : "1x";

        Array.prototype.forEach.call(pictures, function (picture) {
            const sources = picture.querySelectorAll("source");
            let srcset = "";

            for (let i = 0; i < sources.length; i += 1) {
                const media = sources[i].getAttribute("media");

                if (!media || window.matchMedia(media).matches) {
                    srcset = sources[i].getAttribute("srcset") || sources[i].getAttribute("data-srcset") || "";
                    break;
                }
            }

            const candidates = srcset.split(",");
            let url = "";

            for (let i = 0; i < candidates.length; i += 1) {
                const parts = candidates[i].trim().split(/\s+/);

                if (!url || parts[1] === dpr) {
                    url = parts[0];
                }
            }

            if (url && url.indexOf("data:") !== 0) {
                new Image().src = url;
            }
        });
    };

    const prefetch = function (href) {
        if (pages[href] || !supported) {
            return;
        }
        const page = { doc: null, promise: null };

        pages[href] = page;
        page.promise = window.fetch(href, { credentials: "same-origin" }).then(function (response) {
            return response.ok ? response.text() : Promise.reject(response.status);
        }).then(function (html) {
            const doc = new DOMParser().parseFromString(html, "text/html");

            if (!doc.querySelector(".l-content")) {
                throw new Error("no .l-content");
            }
            page.doc = doc;

            /* Картинки hero продукта — после загрузки разводящей, чтобы не мешать её картинкам */
            if (document.readyState === "complete") {
                warmImages(doc);
            } else {
                window.addEventListener("load", function () {
                    warmImages(doc);
                });
            }

            return doc;
        }).catch(function (err) {
            DEBUG && console.log("chooseProduct: prefetch " + href + " не удался — " + err);
            delete pages[href];

            return Promise.reject(err);
        });

        return page.promise;
    };

    /* ── Подмена документа ── */
    const swapHead = function (doc, href) {
        const title = doc.querySelector("title");
        const copyMeta = function (selector, attr) {
            const next = doc.querySelector(selector);
            let current = document.querySelector(selector);

            if (!next) {
                return;
            }
            if (!current) {
                current = document.createElement(next.tagName);
                Array.prototype.forEach.call(next.attributes, function (a) {
                    current.setAttribute(a.name, a.value);
                });
                document.head.appendChild(current);
            }
            current.setAttribute(attr, next.getAttribute(attr) || "");
        };

        if (title) {
            document.title = title.textContent;
        }
        copyMeta("meta[name='description']", "content");
        copyMeta("meta[property='og:title']", "content");
        copyMeta("meta[property='og:description']", "content");
        copyMeta("meta[property='og:url']", "content");
        copyMeta("link[rel='canonical']", "href");

        window.history.pushState({ chooseProduct: true }, "", href);
    };

    const swapContent = function (doc) {
        const current = document.querySelector(".l-content");
        const next = document.importNode(doc.querySelector(".l-content"), true);

        current.parentNode.replaceChild(next, current);

        /* schema.org страницы продукта — стоит после контента, перед подвалом */
        Array.prototype.forEach.call(doc.querySelectorAll("script[type='application/ld+json']"), function (script) {
            const copy = document.createElement("script");

            copy.type = "application/ld+json";
            copy.textContent = script.textContent;
            next.parentNode.insertBefore(copy, next.nextSibling);
        });

        return next;
    };

    /* Модули, которые находят свои элементы на старте, — заново на новом DOM.
       Ленивые (слайдеры, аккордеон, заголовки, ролики) подхватят наблюдатели
       lazyLoading после refresh() */
    const reinitModules = function (content) {
        const modules = itsoftApp.modules;

        if (modules.lazyLoading && modules.lazyLoading.refresh) {
            modules.lazyLoading.refresh();
        }
        if (modules.videoInView && modules.videoInView.refresh) {
            modules.videoInView.refresh();
        }
        ["tComponents", "parallax", "buyingHover", "draggable", "linksWithHash"].forEach(function (name) {
            if (modules[name] && typeof modules[name].init === "function") {
                modules[name].init();
            }
        });
        if (modules.scrollTo && modules.scrollTo.callback) {
            new modules.scrollTo.callback(); // eslint-disable-line no-new
        }

        /* Кнопки поп-апов: слушатели popups.js висят на старых кнопках */
        Array.prototype.forEach.call(content.querySelectorAll(".js-open-popup[data-popup-id]"), function (button) {
            button.addEventListener("click", function (evt) {
                const popup = document.getElementById(button.getAttribute("data-popup-id"));

                evt.preventDefault();

                if (popup && window.openEvent) {
                    popup.dispatchEvent(window.openEvent);
                }
            });
        });
    };

    /* ── Анимация ── */
    const parseBox = function (value) {
        const parts = (value || "0,0,1,1").split(",").map(parseFloat);

        return { x: parts[0], y: parts[1], w: parts[2], h: parts[3] };
    };

    /* FLIP: элемент разводящей → на свою коробку в hero продукта. Коробки заданы
       долями картинок (data-choose-box / data-choose-target-box), совмещаем их,
       масштаб — по ширине коробки. У ноги коробка-цель зависит от кадра hero
       (data-choose-target-boxes — JSON по вариантам, вариант — по currentSrc) */
    const targetBoxFor = function (el, target) {
        const boxes = el.getAttribute("data-choose-target-boxes");

        if (!boxes) {
            return parseBox(el.getAttribute("data-choose-target-box"));
        }

        const map = JSON.parse(boxes);
        const variant = heroVariant(target);

        return parseBox(map[variant] || map.base);
    };

    /* Какой кадр фона hero покажет браузер: по media источников <picture>, а не по
       currentSrc — в момент полёта картинка ещё может грузиться (планшет: без
       этого коробка бралась от базового кадра, нога улетала мимо) */
    const heroVariant = function (img) {
        const picture = img.closest("picture");
        let src = "";

        if (picture) {
            const sources = picture.querySelectorAll("source[srcset]");

            for (let i = 0; i < sources.length; i++) {
                const media = sources[i].getAttribute("media");

                if (!media || window.matchMedia(media).matches) {
                    src = sources[i].getAttribute("srcset");
                    break;
                }
            }
        }

        src = (src || img.currentSrc || img.src || "").split(",")[0].split("/").pop();

        return /-mob/.test(src) ? "mob" : /-tab/.test(src) ? "tab" : /-xl/.test(src) ? "xl" : "base";
    };

    /* Коробка картинки-цели. У <img> с object-fit доли коробки считаются от самого
       отрисованного кадра, а не от бокса: фон hero Т на 1441…1919 — кадр xl (1920×852),
       бокс 853 в высоту, cover режет кадр по краям (на 1512 — по 205 с каждой стороны),
       и нога в hero остаётся 664 px при любой ширине. По боксу масштаб выходил 1.0,
       а место — мимо (заказчик, 24.10.2026). object-position в computed — проценты
       («50% 100%»), px — если так задано в CSS */
    const renderedRect = function (target) {
        const box = target.getBoundingClientRect();

        if (target.tagName !== "IMG" || !target.naturalWidth || !target.naturalHeight) {
            return box;
        }

        const style = window.getComputedStyle(target);
        const fit = style.objectFit;

        if (fit !== "cover" && fit !== "contain") {
            return box;
        }

        const k = fit === "cover"
            ? Math.max(box.width / target.naturalWidth, box.height / target.naturalHeight)
            : Math.min(box.width / target.naturalWidth, box.height / target.naturalHeight);
        const w = target.naturalWidth * k;
        const h = target.naturalHeight * k;
        const pos = style.objectPosition.split(" ");
        const offset = function (value, free) {
            return /%$/.test(value) ? free * parseFloat(value) / 100 : parseFloat(value) || 0;
        };

        return {
            left: box.left + offset(pos[0], box.width - w),
            top: box.top + offset(pos[1] || "50%", box.height - h),
            width: w,
            height: h
        };
    };

    /* Кадр цели ещё не загружен — naturalWidth пуст и коробку не посчитать: ждём load,
       но не дольше WAIT_IMAGE (картинки hero греет prefetch, обычно они уже в кэше) */
    const WAIT_IMAGE = 400;

    const whenImageReady = function (target, callback) {
        if (!target || target.tagName !== "IMG" || target.complete) {
            callback();

            return;
        }

        let done = false;
        const finish = function () {
            if (!done) {
                done = true;
                callback();
            }
        };

        target.addEventListener("load", finish, { once: true });
        target.addEventListener("error", finish, { once: true });
        window.setTimeout(finish, WAIT_IMAGE);
    };

    const fly = function (el, target) {
        if (!el || !target) {
            return;
        }

        const from = el.getBoundingClientRect();
        const to = renderedRect(target);
        const fromBox = parseBox(el.getAttribute("data-choose-box"));
        const toBox = targetBoxFor(el, target);

        const fromW = from.width * fromBox.w;
        const toW = to.width * toBox.w;

        if (!fromW || !toW) {
            return;
        }

        const scale = toW / fromW;
        const fromX = from.left + from.width * fromBox.x;
        const fromY = from.top + from.height * fromBox.y;
        const toX = to.left + to.width * toBox.x;
        const toY = to.top + to.height * toBox.y;
        const dx = toX - fromX - (from.width * fromBox.x) * (scale - 1);
        const dy = toY - fromY - (from.height * fromBox.y) * (scale - 1);

        el.style.transform = "translate(" + dx.toFixed(2) + "px, " + dy.toFixed(2) + "px) scale(" + scale.toFixed(4) + ")";

        /* Клип по карточке открывается плавно: в none переход не анимируется */
        if (el.style.clipPath) {
            el.style.clipPath = "inset(0px 0px 0px 0px)";
            el.style.webkitClipPath = "inset(0px 0px 0px 0px)";
        }
    };

    /* Мобилка: карточки в потоке — перед анимацией закрепляем их в текущих
       координатах, выбранная раскрывается под шапку и до низа экрана */
    const freezeMobile = function (items, chosen) {
        const sectionRect = section.getBoundingClientRect();
        const headerHeight = parseFloat(window.getComputedStyle(section).paddingTop) || 0;
        const rects = Array.prototype.map.call(items, function (item) {
            return item.getBoundingClientRect();
        });

        Array.prototype.forEach.call(items, function (item, i) {
            item.style.position = "absolute";
            item.style.top = (rects[i].top - sectionRect.top) + "px";
            item.style.left = (rects[i].left - sectionRect.left) + "px";
            item.style.width = rects[i].width + "px";
            item.style.height = rects[i].height + "px";
            item.style.margin = "0";
        });

        return function () {
            chosen.style.top = headerHeight + "px";
            chosen.style.height = (window.innerHeight - headerHeight) + "px";
            chosen.style.borderRadius = "0";
        };
    };

    /* Пачка и нога выбранного продукта — из половины в сам слой перехода, в
       своих координатах: половина сейчас раскроется, и элемент внутри неё
       поехал бы и масштабировался вместе с ней (ширина задана долей половины).
       Нога в половине обрезана её краями (clip-path вслед за карточкой): после
       переноса край снимается плавно, вместе с полётом */
    const detach = function (item, el, zIndex, clipToItem) {
        if (!el) {
            return null;
        }

        const rect = el.getBoundingClientRect();
        const sectionRect = section.getBoundingClientRect();

        if (clipToItem) {
            const itemRect = item.getBoundingClientRect();
            const inset = [
                Math.max(0, itemRect.top - rect.top),
                Math.max(0, rect.right - itemRect.right),
                Math.max(0, rect.bottom - itemRect.bottom),
                Math.max(0, itemRect.left - rect.left)
            ].map(function (v) { return v.toFixed(2) + "px"; }).join(" ");

            el.style.clipPath = "inset(" + inset + ")";
            el.style.webkitClipPath = "inset(" + inset + ")";
        }

        el.style.top = (rect.top - sectionRect.top) + "px";
        el.style.left = (rect.left - sectionRect.left) + "px";
        el.style.width = rect.width + "px";
        el.style.height = rect.height + "px";
        el.style.margin = "0";
        el.style.bottom = "auto";
        el.style.right = "auto";
        el.style.zIndex = String(zIndex);
        /* FLIP считает сдвиг от левого верхнего угла — origin такой же */
        el.style.transformOrigin = "0 0";
        section.appendChild(el);

        return el;
    };

    const go = function (item, href) {
        const doc = pages[href].doc;
        const key = item.getAttribute("data-choose");
        const items = section.querySelectorAll(".js-choose-item");
        const mobile = window.matchMedia(MOBILE_QUERY).matches;

        window.scrollTo(0, 0);

        const expandMobile = mobile ? freezeMobile(items, item) : null;

        const pack = detach(item, item.querySelector(".js-choose-pack"), 5, false);
        const figure = detach(item, item.querySelector(".js-choose-figure"), 4, true);

        /* Секция — поверх всего и вне .l-content, который сейчас заменим */
        section.style.height = section.offsetHeight + "px";
        document.body.appendChild(section);
        section.classList.add(LEAVING_CLASS);
        section.style.height = "";

        const content = swapContent(doc);

        swapHead(doc, href);
        reinitModules(content);

        /* Два кадра: новая страница разложена, можно мерить цели в hero */
        const packTarget = document.querySelector(item.getAttribute("data-choose-pack-target"));
        const figureTarget = document.querySelector(item.getAttribute("data-choose-figure-target"));

        window.requestAnimationFrame(function () {
            window.requestAnimationFrame(function () {
                whenImageReady(figureTarget, function () {
                    fly(pack, packTarget);
                    fly(figure, figureTarget);

                    if (expandMobile) {
                        expandMobile();
                    }
                    section.classList.add(LEAVING_CLASS + "-" + key);
                    section.classList.add(FADING_CLASS);

                    window.setTimeout(function () {
                        section.parentNode.removeChild(section);
                    }, TIME + FADE_DELAY + 50);
                });
            });
        });
    };

    const onClick = function (evt) {
        const item = evt.currentTarget;
        const href = item.getAttribute("href");

        if (evt.defaultPrevented || evt.button !== 0 || evt.metaKey || evt.ctrlKey || evt.shiftKey || evt.altKey) {
            return;
        }
        if (section.classList.contains(LEAVING_CLASS) || section.classList.contains(PENDING_CLASS)) {
            evt.preventDefault();

            return;
        }
        if (!pages[href] || !pages[href].promise) {
            return;
        }

        evt.preventDefault();

        if (pages[href].doc) {
            go(item, href);

            return;
        }

        waitAndGo(item, href);
    };

    /* Страница ещё качается: ждём её, но не дольше WAIT, иначе обычный переход */
    const waitAndGo = function (item, href) {
        section.classList.add(PENDING_CLASS);

        const timer = new Promise(function (resolve, reject) {
            window.setTimeout(function () {
                reject(new Error("timeout"));
            }, WAIT);
        });

        Promise.race([pages[href].promise, timer]).then(function () {
            go(item, href);
        }).catch(function (err) {
            DEBUG && console.log("chooseProduct: " + href + " не дождались — " + err);
            window.location.href = href;
        });
    };

    const items = section.querySelectorAll(".js-choose-item");

    Array.prototype.forEach.call(items, function (item) {
        item.addEventListener("click", onClick);
    });

    if (supported) {
        /* HTML обеих страниц — сразу: это ~200 КБ текста, картинкам разводящей не
           мешает, а на Bitrix до load проходят секунды. Наведение / касание —
           повтор, если первая попытка не удалась */
        const retry = function (evt) {
            prefetch(evt.currentTarget.getAttribute("href"));
        };

        Array.prototype.forEach.call(items, function (item) {
            prefetch(item.getAttribute("href"));
            item.addEventListener("mouseenter", retry);
            item.addEventListener("touchstart", retry, { passive: true });
            item.addEventListener("focus", retry);
        });

        /* Клик случился до загрузки сборки (инлайн-скрипт index.php): доигрываем переход */
        if (window.chooseEarlyHref) {
            const earlyHref = window.chooseEarlyHref;
            const earlyItem = section.querySelector(".js-choose-item[href='" + earlyHref + "']");

            window.clearTimeout(window.chooseEarlyTimer);
            window.chooseEarlyHref = null;

            if (earlyItem && pages[earlyHref]) {
                waitAndGo(earlyItem, earlyHref);
            } else {
                window.location.href = earlyHref;
            }
        }

        window.addEventListener("popstate", function () {
            window.location.reload();
        });
    } else if (window.chooseEarlyHref) {
        window.clearTimeout(window.chooseEarlyTimer);
        window.location.href = window.chooseEarlyHref;
    }

    module.callback = {
        prefetch: prefetch,
        go: go
    };
    module.pages = pages;
});
